import { DemoTag } from "@components/common/demo-tag";
import { ProjectActions } from "@components/common/project-actions";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { ProjectManagerDisplay } from "@components/project/project-manager-display";
import { PROJECTS_TABLE_COLUMN_LABELS } from "@components/table/projects/projects-table-constants";
import {
  ProjectsTableColumnField,
  ProjectsTableGridColDef,
} from "@components/table/projects/projects-table-types";
import styles from "@components/table/projects/projects-table.module.css";
import { ProjectTableContextType } from "@components/table/projects/use-projects-table-context";
import { UserAvatarWithMenu } from "@components/user-more-info-menu/user-avatar-with-menu";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import {
  AccessLevelDisplayNames,
  SdbProject,
} from "@custom-types/project-types";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { Box, Stack } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import {
  generateGroupDetailsRoute,
  generateProjectDetailsRoute,
} from "@router/router-helper";
import { withEllipsis } from "@styles/common-styles";
import {
  defaultProjectThumbnail,
  getProjectCreatedElapsed,
  getProjectGroupId,
  getProjectGroupName,
  getProjectModifiedElapsed,
  getProjectSize,
  isProjectDemo,
} from "@utils/project-utils";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { DASH, SHOW_ON_HOVER_CLASS } from "@utils/ui-utils";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Text } from "@flat-ui";
import { FaroTableColumnVisibilityMenuIcon } from "@components/common/faro-table/faro-table-column-visibility-menu-icon";
import { useColumnPreferencesContext } from "@context-providers/table/column-preferences-provider";
import { TableHeaderWithDraggableIcon } from "@components/table/table-header-with-draggable-icon";
import { isProjectsTableColumnField } from "@components/table/projects/projects-table-utils";
import { getPrettyDateElapsed } from "@utils/data-display";

/**
 * FLEX_WEIGHTS defines the relative flex-grow weights for resizable columns in the projects table.
 * Higher values make columns expand more when there is extra space.
 * Only columns listed here will have a custom flex value; others use the default.
 */
const FLEX_WEIGHTS: Partial<Record<ProjectsTableColumnField, number>> = {
  name: 1,
};

/**
 * FIXED_MAX_WIDTHS sets the maximum width (in pixels) for specific columns in the projects table.
 * This prevents columns from growing too wide, especially for columns with limited content.
 * Only columns listed here will have a maxWidth restriction.
 */
const FIXED_MAX_WIDTHS: Partial<Record<ProjectsTableColumnField, number>> = {
  projectManager: 200,
  managerAvatar: 54,
  group: 200,
  created: 150,
  lastCaptured: 150,
  lastUpdated: 200,
  client: 200,
  size: 120,
  accessLevel: 120,
  tags: 120,
  options: 90,
};

/**
 * FIXED_MIN_WIDTHS sets the minimum width (in pixels) for specific columns in the projects table.
 * This ensures important columns remain readable and do not shrink too much when space is limited.
 * Only columns listed here will have a minWidth restriction.
 */
const FIXED_MIN_WIDTHS: Partial<Record<ProjectsTableColumnField, number>> = {
  name: 250,
  projectManager: 175,
  managerAvatar: 40,
  group: 150,
  created: 150,
  lastUpdated: 150,
  client: 150,
  lastCaptured: 150,
  size: 90,
  accessLevel: 120,
  tags: 90,
  options: 90,
};

/** The width of the thumbnail image in pixels */
const THUMBNAIL_IMAGE_WIDTH = 70;

interface Props extends BaseCompanyIdProps {
  /** The context type for the project table */
  contextType: ProjectTableContextType;
}

/**
 * @returns the columns definition to be used in the projects table
 */
export function useProjectsTableColumns({
  companyId,
  contextType,
}: Props): ProjectsTableGridColDef[] {
  const { canViewCompanyGroups } = useHasUserValidFeatureCompanyLevel();
  const { trackEvent } = useTrackEvent();

  const { columnPreferences, visibleOrderedColumns } =
    useColumnPreferencesContext();

  const columnsMap: Record<ProjectsTableColumnField, ProjectsTableGridColDef> =
    useMemo(() => {
      return {
        name: {
          field: "name",
          headerName: PROJECTS_TABLE_COLUMN_LABELS.name,
          flex: FLEX_WEIGHTS.name,
          maxWidth: FIXED_MAX_WIDTHS.name,
          minWidth: FIXED_MIN_WIDTHS.name,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
          sortable: true,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          disableReorder: true,
          renderCell: (
            params: GridRenderCellParams<{ entity: SdbProject }>
          ) => {
            return (
              <Stack
                direction="row"
                alignItems="center"
                gap="0.625rem"
                sx={{ ...withEllipsis }}
              >
                <Box
                  component="img"
                  src={
                    params.row.entity.thumbnailUrl ?? defaultProjectThumbnail
                  }
                  sx={{
                    height: "50px",
                    width: `${THUMBNAIL_IMAGE_WIDTH}px`,
                    objectFit: "cover",
                  }}
                  alt="Project thumbnail"
                />

                <SphereTooltip
                  title={<var>{params.row.entity.name}</var>}
                  boxProps={{
                    sx: {
                      ...withEllipsis,
                      fontWeight: "bold",
                    },
                  }}
                >
                  <Link
                    to={generateProjectDetailsRoute(
                      companyId,
                      params.row.entity.id
                    )}
                    onClick={(e) => {
                      trackEvent({
                        name: ProjectEvents.openProjectDetails,
                        props: {
                          projectId: params.row.entity.id,
                          eventLocation: contextType,
                          viewMode: "list",
                        },
                      });
                      // to + event replicate onRowClick as in projects-table.tsx
                      e.stopPropagation();
                    }}
                    className={styles["removed-underline"]}
                  >
                    <var>{params.row.entity.name}</var>
                  </Link>
                </SphereTooltip>
              </Stack>
            );
          },
        },

        managerAvatar: {
          field: "managerAvatar",
          headerName: "",
          flex: FLEX_WEIGHTS.managerAvatar,
          maxWidth: FIXED_MAX_WIDTHS.managerAvatar,
          minWidth: FIXED_MIN_WIDTHS.managerAvatar,
          align: "right",
          renderCell: (
            params: GridRenderCellParams<{ entity: SdbProject }>
          ) => {
            return (
              <Box
                component="div"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  pr: "0px",
                }}
              >
                <UserAvatarWithMenu
                  member={params.row.entity.managers?.projectManager}
                />
              </Box>
            );
          },
          // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
          sortable: false,
        },

        projectManager: {
          field: "projectManager",
          headerName: PROJECTS_TABLE_COLUMN_LABELS.projectManager,
          flex: FLEX_WEIGHTS.projectManager,
          maxWidth: FIXED_MAX_WIDTHS.projectManager,
          minWidth: FIXED_MIN_WIDTHS.projectManager,
          renderHeader: (params) => (
            <TableHeaderWithDraggableIcon
              title={params.colDef.headerName ?? ""}
            />
          ),
          renderCell: (
            params: GridRenderCellParams<{ entity: SdbProject }>
          ) => {
            return (
              <Box sx={{ ...withEllipsis }}>
                <ProjectManagerDisplay
                  project={params.row.entity}
                  companyId={companyId}
                  canViewAllCompanyGroups={canViewCompanyGroups}
                />
              </Box>
            );
          },
          // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
          sortable: false,
        },

        created: {
          field: "created",
          headerName: PROJECTS_TABLE_COLUMN_LABELS.created,
          flex: FLEX_WEIGHTS.created,
          maxWidth: FIXED_MAX_WIDTHS.created,
          minWidth: FIXED_MIN_WIDTHS.created,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
          sortable: true,
          renderHeader: (params) => (
            <TableHeaderWithDraggableIcon
              title={params.colDef.headerName ?? ""}
            />
          ),
          renderCell: (
            params: GridRenderCellParams<{ entity: SdbProject }>
          ) => {
            return <var>{getProjectCreatedElapsed(params.row.entity)}</var>;
          },
        },

        lastUpdated: {
          field: "lastUpdated",
          headerName: PROJECTS_TABLE_COLUMN_LABELS.lastUpdated,
          flex: FLEX_WEIGHTS.lastUpdated,
          maxWidth: FIXED_MAX_WIDTHS.lastUpdated,
          minWidth: FIXED_MIN_WIDTHS.lastUpdated,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
          sortable: true,
          renderHeader: (params) => (
            <TableHeaderWithDraggableIcon
              title={params.colDef.headerName ?? ""}
            />
          ),
          renderCell: (
            params: GridRenderCellParams<{ entity: SdbProject }>
          ) => {
            return <var>{getProjectModifiedElapsed(params.row.entity)}</var>;
          },
        },

        group: {
          field: "group",
          headerName: PROJECTS_TABLE_COLUMN_LABELS.group,
          flex: FLEX_WEIGHTS.group,
          maxWidth: FIXED_MAX_WIDTHS.group,
          minWidth: FIXED_MIN_WIDTHS.group,
          renderHeader: (params) => (
            <TableHeaderWithDraggableIcon
              title={params.colDef.headerName ?? ""}
            />
          ),
          renderCell: (
            params: GridRenderCellParams<{ entity: SdbProject }>
          ) => {
            const groupId = getProjectGroupId(params.row.entity);

            const contentSphereTooltip = (
              <SphereTooltip
                title={<var>{getProjectGroupName(params.row.entity)}</var>}
              >
                <var>{getProjectGroupName(params.row.entity)}</var>
              </SphereTooltip>
            );

            if (groupId && canViewCompanyGroups) {
              return (
                <Link
                  to={generateGroupDetailsRoute(companyId, groupId)}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className={styles["removed-underline"]}
                >
                  <Box sx={{ ...withEllipsis }}>{contentSphereTooltip}</Box>
                </Link>
              );
            } else {
              return contentSphereTooltip;
            }
          },
          // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
          sortable: false,
        },

        accessLevel: {
          field: "accessLevel",
          headerName: PROJECTS_TABLE_COLUMN_LABELS.accessLevel,
          flex: FLEX_WEIGHTS.accessLevel,
          maxWidth: FIXED_MAX_WIDTHS.accessLevel,
          minWidth: FIXED_MIN_WIDTHS.accessLevel,
          renderHeader: (params) => (
            <TableHeaderWithDraggableIcon
              title={params.colDef.headerName ?? ""}
            />
          ),
          renderCell: (
            params: GridRenderCellParams<{ entity: SdbProject }>
          ) => {
            const accessLevel = params.row.entity.accessLevel;
            return AccessLevelDisplayNames[accessLevel];
          },
          // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
          sortable: false,
        },

        tags: {
          field: "tags",
          headerName: PROJECTS_TABLE_COLUMN_LABELS.tags,
          flex: FLEX_WEIGHTS.tags,
          maxWidth: FIXED_MAX_WIDTHS.tags,
          minWidth: FIXED_MIN_WIDTHS.tags,
          renderCell: (
            params: GridRenderCellParams<{ entity: SdbProject }>
          ) => {
            return isProjectDemo(params.row.entity) ? <DemoTag /> : "";
          },
          // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
          sortable: false,
        },

        options: {
          field: "options",
          type: "actions",
          align: "right",
          flex: FLEX_WEIGHTS.options,
          maxWidth: FIXED_MAX_WIDTHS.options,
          minWidth: FIXED_MIN_WIDTHS.options,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          disableReorder: true,
          renderHeader: () => (
            <FaroTableColumnVisibilityMenuIcon
              columnPreferences={columnPreferences}
            />
          ),
          renderCell: (
            params: GridRenderCellParams<{ entity: SdbProject }>
          ) => {
            return (
              <Box
                component="div"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "right",
                  // This will be overridden by parent component using SHOW_ON_HOVER_CLASS
                  visibility: "hidden",
                }}
                className={SHOW_ON_HOVER_CLASS}
              >
                <ProjectActions
                  project={params.row.entity}
                  contextType={
                    contextType === "group projects"
                      ? "group-project-list-view"
                      : "project-list-view"
                  }
                />
              </Box>
            );
          },
          // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
          sortable: false,
        },

        client: {
          field: "client",
          headerName: PROJECTS_TABLE_COLUMN_LABELS.client,
          flex: FLEX_WEIGHTS.client,
          maxWidth: FIXED_MAX_WIDTHS.client,
          minWidth: FIXED_MIN_WIDTHS.client,
          renderHeader: (params) => (
            <TableHeaderWithDraggableIcon
              title={params.colDef.headerName ?? ""}
            />
          ),
          renderCell: (
            params: GridRenderCellParams<{ entity: SdbProject }>
          ) => {
            return (
              <Text variant="bodyS">
                <var>{params.row.entity.clientName || DASH}</var>
              </Text>
            );
          },
          // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
          sortable: false,
        },

        size: {
          field: "size",
          headerName: PROJECTS_TABLE_COLUMN_LABELS.size,
          flex: FLEX_WEIGHTS.size,
          maxWidth: FIXED_MAX_WIDTHS.size,
          minWidth: FIXED_MIN_WIDTHS.size,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
          sortable: true,
          renderHeader: (params) => (
            <TableHeaderWithDraggableIcon
              title={params.colDef.headerName ?? ""}
            />
          ),
          renderCell: (
            params: GridRenderCellParams<{ entity: SdbProject }>
          ) => {
            return (
              <Text variant="bodyS" component="var">{getProjectSize(params.row.entity)}</Text>
            );
          },
        },

        lastCaptured: {
          field: "lastCaptured",
          headerName: PROJECTS_TABLE_COLUMN_LABELS.lastCaptured,
          flex: FLEX_WEIGHTS.lastCaptured,
          maxWidth: FIXED_MAX_WIDTHS.lastCaptured,
          minWidth: FIXED_MIN_WIDTHS.lastCaptured,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by external package
          sortable: true,
          renderHeader: (params) => (
            <TableHeaderWithDraggableIcon
              title={params.colDef.headerName ?? ""}
            />
          ),
          renderCell: (
            params: GridRenderCellParams<{ entity: SdbProject }>
          ) => {
            const lastCapturedAt = params.row.entity.lastCapturedAt;
            if (!lastCapturedAt) {
              return DASH;
            }

            return getPrettyDateElapsed(lastCapturedAt);
          },
        },
      };
    }, [
      canViewCompanyGroups,
      columnPreferences,
      companyId,
      contextType,
      trackEvent,
    ]);

  const orderedColumns = visibleOrderedColumns
    .filter(isProjectsTableColumnField)
    .map((header) => columnsMap[header]);

  return orderedColumns;
}
